import {useQuery} from "@apollo/client";
import {
    DeliveryOrderListResponse,
    GET_ORDER_LISTING_FOR_DELIVERY,
} from "../graphql/queries/getOrderListingForDelivery";
import {useEffect, useState} from "react";
import {formatDateTimeRangeToISO} from "../../../../utils/dateTimeUtils";
import {OrderType} from "../../../../graphql/types/OrderType";
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";

export interface ParcelQueryResult {
    parcelData: OrderType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    buildQueryVariables: () => any;
}

export const useParcelQuery = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number,
    warehouseId: string | null,
    sortField?: string | null,
    sortDirection?: 'ASC' | 'DESC' | null
): ParcelQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);

    const buildQueryVariables = () => {
        const timeRanges = {
            orderTime: formatDateTimeRangeToISO(filters.orderTime),
            receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
            destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
            destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
            deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
            markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
            deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
            lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
            updatedAt: formatDateTimeRangeToISO(filters.updatedAt),
            returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
        };

        // 处理 receiverZipcode：如果有原始输入，转换为数组（与 trackingNumbers 一致）
        let receiverZipcode: string[] | undefined = filters.receiverZipcode;
        if (filters.receiverZipcodeInput) {
            // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
            const zipcodes = filters.receiverZipcodeInput
                .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
                .map(z => z.trim())
                .filter(z => z.length > 0);
            receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
        }
        
        // 构建查询过滤器，避免单值和多值字段冲突
        const queryFilters: any = {
            ...filters,
            ...timeRanges,
            // 转换数组类型为多值查询
            channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
            pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
            overallStatuses: filters.overallStatus || undefined,
            nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
            deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
            receiverZipcode: receiverZipcode, // 使用转换后的数组
            pickupTaskDate: undefined, // 不发送到后端
            // 转换 hubId 和 warehouseId 为数字类型
            hubId: filters.hubId ? parseInt(filters.hubId) : undefined,
            warehouseId: filters.warehouseId ? parseInt(filters.warehouseId) : undefined
        };
        
        // 清空临时输入字段，不发送到后端
        queryFilters.receiverZipcodeInput = undefined;
        queryFilters.pickupTaskDate = undefined;

        // 清空单值字段，避免冲突
        queryFilters.channelId = undefined;
        queryFilters.pickupHubId = undefined;
        queryFilters.overallStatus = undefined;
        queryFilters.nextAction = undefined;
        queryFilters.deliveredDspId = undefined;

        // 移除无效的时间范围字段，避免GraphQL schema错误
        Object.keys(queryFilters).forEach(key => {
            const value = queryFilters[key];
            if (value === undefined) {
                delete queryFilters[key];
                return;
            }
            if (typeof value === 'object' && value !== null) {
                const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
                const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
                if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                    delete queryFilters[key];
                }
            }
        });

        return {
            warehouseId: warehouseId,
            filters: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: sortField || "orderTime",
                sortDirection: sortDirection || "DESC"
            }
        };
    };

    // 执行查询
    const { data, loading: queryLoading, refetch } = useQuery<DeliveryOrderListResponse>(
        GET_ORDER_LISTING_FOR_DELIVERY , {variables: buildQueryVariables(), skip: warehouseId === null}
    );

    // 当数据变化时更新状态
    useEffect(() => {
        const responseData = data?.getOrderListingForDelivery?.content;
        if (responseData) {
            let orderedData = responseData;
            const trackingInput = filters.trackingNumbers;
            // 只有在没有用户主动排序时，才使用运单号顺序排序
            // 如果用户点击了列排序（sortField 不为空），则使用后端排序结果
            if (trackingInput && trackingInput.trim().length > 0 && !sortField) {
                const trackingList = trackingInput
                    .split(/[\r\n,\s]+/)
                    .map(t => t.trim())
                    .filter(t => t.length > 0);
                if (trackingList.length > 0) {
                    const orderMap = new Map<string, number>();
                    trackingList.forEach((tracking, idx) => {
                        orderMap.set(tracking.toUpperCase(), idx);
                    });
                    orderedData = [...responseData].sort((a, b) => {
                        const aKey = (a.trackingNumber || '').toUpperCase();
                        const bKey = (b.trackingNumber || '').toUpperCase();
                        const aIndex = orderMap.has(aKey) ? (orderMap.get(aKey) as number) : Number.MAX_SAFE_INTEGER;
                        const bIndex = orderMap.has(bKey) ? (orderMap.get(bKey) as number) : Number.MAX_SAFE_INTEGER;
                        if (aIndex !== bIndex) {
                            return aIndex - bIndex;
                        }
                        return 0;
                    });
                }
            }
            setParcelData(orderedData);
            setLoading(false);
        }
        const totalElements = data?.getOrderListingForDelivery?.totalElements;
        if (totalElements) {
            setTotal(totalElements);
        }
    }, [data, filters.trackingNumbers, sortField]);

    return {
        parcelData,
        loading: loading || queryLoading,
        refetch,
        totalElements: total,
        buildQueryVariables
    };
}; 